import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
  Signal,
} from '@angular/core';
import { HelpWidgetFactory } from 'src/app/ajs-upgraded-providers';
import { DownloadService } from '../../services/download.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import { CurrentPlanService } from 'src/app/components/plans/current-plan.service';
import { PromiseUtilsService } from 'src/app/shared/services/promise-utils.service';
import {
  StorageFile,
  StorageFilesDetails,
  StorageSelectorFilter,
  StorageSelectorType,
  StorageUtilsService,
} from '../../services/storage-utils.service';
import { StorageService } from '../../services/storage.service';
import { NewFolderModalComponent } from '../new-folder-modal/new-folder-modal.component';

import { StorageApiService } from 'src/app/api/services/storage-api.service';
import { UploadComponent } from '../upload/upload.component';
import {
  ListItem,
  ScrollingListStateService,
} from 'src/app/components/scrolling-list/services/scrolling-list-state.service';
import {
  PaginatedScrollingListService,
  WithListFileResponse,
} from 'src/app/components/scrolling-list/services/paginated-scrolling-list.service';
import { ListSearchStateService } from 'src/app/components/scrolling-list/services/list-search-state.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { StorageGridViewComponent } from '../storage-grid-view/storage-grid-view.component';
import {
  BatchActionHandlerService,
  ListOperation,
  ListOperations,
} from 'src/app/components/scrolling-list';
import { StorageApiSearch } from 'src/app/api/services/api-utils.service';
import { StateService } from '@uirouter/core';
import { RvDraggableService } from 'src/app/rv-draggable/services/rv-draggable.service';

@Component({
  selector: 'files-list',
  templateUrl: './files-list.component.html',
  styleUrls: ['./files-list.component.scss'],
  standalone: false,
  providers: [
    { provide: 'WithListService', useExisting: StorageApiService },
    { provide: 'ListKind', useValue: 'file' },
    { provide: ListSearchStateService },
    { provide: ScrollingListStateService },
    { provide: BatchActionHandlerService },
    { provide: PaginatedScrollingListService },
  ],
})
export class FilesListComponent implements OnInit {
  @ViewChild('upload') uploadComponent?: UploadComponent;
  @ViewChild('storageGridView') storageGridView?: StorageGridViewComponent;

  @Input() folderPath: string = '';
  @Input() isListView: boolean = false;
  @Input() allowUpload: boolean = true;
  @Input() allowBatchOperations: boolean = true;
  @Input() excludedFiles: string[] = [];

  @Input({ required: true }) selectorType: StorageSelectorType;
  @Input({ required: true }) selectorFilter: StorageSelectorFilter;
  @Input({ required: true }) storageFull: boolean;

  @Output() filesSelected: EventEmitter<StorageFile[]> = new EventEmitter<
    StorageFile[]
  >();
  @Output() selectedItemsCount: EventEmitter<number> =
    new EventEmitter<number>();
  @Output() isListViewChange: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  get selectedFolders(): ListItem<StorageFile>[] {
    return this.listState
      .getSelected()
      .filter((file) => file.item.kind === 'folder');
  }
  get selectedFiles(): ListItem<StorageFile>[] {
    return this.listState
      .getSelected()
      .filter((file) => file.item.kind === 'file');
  }

  search: StorageApiSearch = {};

  uploadQueueLength = 0;
  lastClickTime = 0;
  fileListStatus = {
    code: 200,
  };

  listOperations = {
    name: 'File',
    getStorageSearchApi: () => this.search,
    operations: [
      {
        name: 'Download',
        actionCall: async (file: ListItem<StorageFile>) => {
          if (!this.allowBatchOperations) this.listState.setLoadingItems(true);
          try {
            await this.promiseUtilsService.delay(100);
            if (this.storageUtilsService.fileIsFolder(file.item)) {
              await this.downloadService.downloadFolder(file.item);
            } else {
              await this.downloadService.downloadFile(file.item);
            }
          } catch (error) {
            if (error.message) {
              error.category = 'download';
            }
            throw error;
          } finally {
            this.listState.setLoadingItems(false);
          }
        },
        afterOperationBatchActions: async () => {
          await this.promiseUtilsService.delay(1000);
          this.downloadService.cleanUp();
        },
        mainOperation: true,
        showIn: ['bulk', 'single'],
        icon: 'download',
      },
      {
        name: 'Make a Copy',
        actionCall: async (file: ListItem<StorageFile>) => {
          if (!this.allowBatchOperations) this.listState.setLoadingItems(true);
          try {
            await this.storageApiService.duplicate(file.item.name);
          } catch (error) {
            if (error.message) {
              error.category = 'duplicate';
            }
            throw error;
          } finally {
            this.listState.setLoadingItems(false);
          }
        },
        afterOperationBatchActions: async () => {
          await this.promiseUtilsService.delay(2000); // in order to have thumbnails loaded
          this.reloadList();
        },
        hidden: () => {
          return this.isTrashFolder;
        },
        showIn: ['bulk', 'single'],
        icon: 'duplicate',
      },
      {
        name: 'Move',
        beforeBatchAction: async (selectedItems) => {
          this.listState.setLoadingItems(false);
          await this._showBreakLinkWarning();
          const destination = await this.storageService.showFolderSelectorModal(
            this.folderPath,
            selectedItems.map((file) => file.item.name),
          );
          const folder = destination[0];
          return folder.name === '/' ? '' : folder.name;
        },
        actionCall: async (
          file: ListItem<StorageFile>,
          destination: string,
        ) => {
          if (!this.allowBatchOperations) this.listState.setLoadingItems(true);
          try {
            const newPath =
              destination + this.storageUtilsService.fileName(file.item);
            return await this.storageApiService.rename(file.item.name, newPath);
          } catch (error) {
            if (error.message) {
              error.category = 'move';
            }
            throw error;
          } finally {
            this.listState.setLoadingItems(false);
          }
        },
        reloadList: true,
        hidden: () => {
          return this.isTrashFolder;
        },
        mainOperation: true,
        showIn: ['bulk', 'single'],
        icon: 'move-to-folder',
      },
      {
        name: 'Copy URL',
        beforeBatchAction: (selected: ListItem<StorageFile>[]) => {
          this.listState.setLoadingItems(false);
          return this.storageService.copyFileUrl(selected[0]);
        },
        hidden: () => {
          return this.isTrashFolder;
        },
        showIn: ['single'],
        icon: 'copy',
      },
      {
        name: 'Rename',
        beforeBatchAction: async (files: ListItem<StorageFile>[]) => {
          this.listState.setLoadingItems(false);
          await this._showBreakLinkWarning();
          return this.storageService.showRenameFileModal(files[0]);
        },
        actionCall: async (file: ListItem<StorageFile>, name: string) => {
          if (!this.allowBatchOperations) this.listState.setLoadingItems(true);
          try {
            const suffix =
              this.storageUtilsService.fileIsFolder(file.item) &&
              !this.storageUtilsService.fileIsFolder({
                name: name,
                kind: 'file',
              })
                ? '/'
                : '';
            const newName = name + suffix;
            return await this.storageApiService.rename(file.item.name, newName);
          } catch (error) {
            if (error.message) {
              error.category = 'rename';
            }
            throw error;
          } finally {
            this.listState.setLoadingItems(false);
          }
        },
        hidden: () => {
          return this.listState.getSelected().length > 1 || this.isTrashFolder;
        },
        showIn: ['single'],
        reloadList: true,
        icon: 'pencil-line',
      },
      {
        name: 'Move To Trash',
        beforeBatchAction: (selection) => {
          this.listState.setLoadingItems(false);
          return this._showBreakLinkWarning(selection, true);
        },
        actionCall: async (selectedFiles: { items: StorageFile[] }) => {
          if (!this.allowBatchOperations) this.listState.setLoadingItems(true);
          try {
            return await this.storageApiService.moveToTrash(selectedFiles);
          } catch (error) {
            if (error.message) {
              error.category = 'move-to-trash';
            }
            throw error;
          } finally {
            this.listState.setLoadingItems(false);
          }
        },
        groupBy: true,
        reloadList: true,
        hidden: () => {
          return this.isTrashFolder;
        },
        showIn: ['bulk', 'single'],
        icon: 'trash',
        mainOperation: true,
        danger: true,
      },
      {
        name: 'Restore From Trash',
        actionCall: async (selectedFiles: { items: StorageFile[] }) => {
          if (!this.allowBatchOperations) this.listState.setLoadingItems(true);
          try {
            return await this.storageApiService.restoreFromTrash(selectedFiles);
          } catch (error) {
            throw error;
          } finally {
            this.listState.setLoadingItems(false);
          }
        },
        groupBy: true,
        reloadList: true,
        hidden: () => {
          return !this.isTrashFolder;
        },
      },
      {
        name: 'Delete Permanently',
        beforeBatchAction: () => {
          this.listState.setLoadingItems(false);
          let title: string, message: string;
          const single = this.listState.getSelected().length === 1;

          if (this.selectedFiles.length && this.selectedFolders.length) {
            title = 'files and folders';
            message = 'these ' + title;
          } else {
            if (this.selectedFolders.length) {
              title = 'folder' + (single ? '' : 's');
            } else {
              title = 'file' + (single ? '' : 's');
            }

            message =
              (single
                ? 'this'
                : 'these ' + this.listState.getSelected().length) +
              ' ' +
              title;
          }

          return this.modalService.confirmDanger(
            'Delete ' + title + '?',
            'Are you sure you want to delete ' + message + '?',
            'Delete Forever',
          );
        },
        actionCall: async (selectedFiles: { items: StorageFile[] }) => {
          if (!this.allowBatchOperations) this.listState.setLoadingItems(true);
          try {
            return await this.storageApiService.deleteFiles(selectedFiles);
          } catch (error) {
            if (error.message) {
              error.category = 'delete';
            }
            throw error;
          } finally {
            this.listState.setLoadingItems(false);
          }
        },
        groupBy: true,
        reloadList: true,
        hidden: () => {
          return !this.isTrashFolder;
        },
      },
    ],
  } as ListOperations<StorageFile>;

  filterConfig = {
    placeholder: 'Search for files or folders',
    id: 'storageSelectorSearchInput',
    borderless: true,
  };

  @ViewChild('filesListContainer') filesListContainer: ElementRef;

  get isTrashFolder(): boolean {
    return this.storageUtilsService.isTrashFolder(this.folderPath);
  }

  protected listService = inject<PaginatedScrollingListService<StorageFile>>(
    PaginatedScrollingListService,
  );
  protected listState = inject(ScrollingListStateService<StorageFile>);
  protected list: Signal<ListItem<StorageFile>[]> = this.listService.list;
  protected loadingItems = toSignal(this.listState.loadingItems());
  protected error = toSignal(this.listState.error());

  protected draggableService:
    | RvDraggableService<StorageFile, StorageFile>
    | undefined;

  constructor(
    public currentPlanService: CurrentPlanService,
    public storageService: StorageService,
    private storageApiService: StorageApiService,
    private storageUtilsService: StorageUtilsService,
    private modalService: ModalService,
    private downloadService: DownloadService,
    private promiseUtilsService: PromiseUtilsService,
    public helpWidgetFactory: HelpWidgetFactory,
    private stateService: StateService,
  ) {
    this.listService.processFileApiResponse = this.processApiResponse;
  }

  ngOnInit(): void {
    // Get folderPath from state params if not provided via @Input
    if (!this.folderPath) {
      this.folderPath = this.stateService.params.folderPath || '';
    }

    this.search = {
      query: undefined,
      doSearch: () => {
        if (this.storageGridView) {
          this.storageGridView.checkSearchChange();
        }
      },
      sortBy: 'name',
      reverse: false,
      folderPath: this.folderPath,
    };

    this.listService.load(this.search);

    this.listService.setOperations(this.listOperations);

    this.initDraggableService();
  }

  private initDraggableService(): void {
    if (!this.draggableService) {
      this.draggableService = new RvDraggableService<StorageFile, StorageFile>({
        items: this.listService
          .list()
          .map((item) => item.item) as StorageFile[],
        getSelectedItems: this.listState.getSelected,
        onItemDrop: this.handleItemDrop,
        containersSelector: '.files-list-container, .folder-path',
        appendTo: '.files-list-container',
        draggableIdProperty: 'name',
        droppableIdProperty: 'name',
      });
    }
  }

  doSearch = () => {
    this.search.doSearch();
  };

  private handleItemDrop = async (
    itemNames: string[],
    folderPath: string,
    idProperty: string,
  ) => {
    const items = this.listService
      .list()
      .filter((item: ListItem<StorageFile>) =>
        itemNames.includes(item.item[idProperty]),
      );
    let operationName;
    try {
      if (folderPath === StorageUtilsService.TRASH_FOLDER) {
        operationName = 'Move To Trash';
        await this._showBreakLinkWarning(items, true);
      } else {
        operationName = 'Move';
        await this._showBreakLinkWarning();
      }
    } catch {
      return;
    }
    this.handleBulkOperation(operationName, items, folderPath);
  };

  private async handleBulkOperation(
    operationName: string,
    items: ListItem<StorageFile>[],
    folderPath: string,
  ) {
    const operation: ListOperation<StorageFile> =
      this.listOperations.operations.find(
        (operation) => operation.name === operationName,
      );
    if (operation && operation.onClick) {
      this.listService.activeOperation = operation;
      operation.onClick(true, items, folderPath, this.search);
    }
  }

  private async _showBreakLinkWarning(
    selection = [],
    isDelete: boolean = false,
  ) {
    let deleteMessage = isDelete
      ? `<strong>${selection.length}</strong> ${selection.length === 1 ? 'item' : 'items'} will be sent to the trash folder. It will stay there until you restore it, or permanently delete it.<br /><br />`
      : '';
    deleteMessage +=
      "Deleting or modifying files will break any links to existing Presentations. If you are using this asset in a Presentation, you'll need to manually update those links in the Editor.";
    return await this.modalService.confirmDanger('Warning!', deleteMessage);
  }

  processApiResponse = (apiResponse: WithListFileResponse) => {
    const parentFolder = this.folderPath;
    const { files, ...filesDetails } = apiResponse;
    const result = {
      items: [] as ListItem<StorageFile>[],
      filesDetails: filesDetails as StorageFilesDetails,
    };

    const listItems: ListItem<StorageFile>[] = files?.length
      ? files.map((file) => this.listService.mapItem(file))
      : [];

    result.items = listItems.filter((file) => {
      let include = true;

      if (this.excludedFiles.indexOf(file.item.name) !== -1) {
        include = false;
      }

      if (file.item.name === parentFolder) {
        include = false;
      }

      if (
        this.storageService.isFolderFilter(this.selectorFilter) &&
        !this.storageUtilsService.fileIsFolder(file.item)
      ) {
        include = false;
      }

      return include;
    });
    this.listState.filesDetails = filesDetails;

    if (!this.folderPath || !parentFolder || parentFolder === '/') {
      // We can push it to the end of the list because the items will be sorted after
      result.items.push({
        item: {
          name: StorageUtilsService.TRASH_FOLDER,
          kind: 'folder',
        },
        isFolder: false,
        selected: false,
      });
    }

    return result;
  };

  reloadList() {
    this.listService.reload(this.search);
    this.doSearch();
  }

  async createFolder() {
    if (!this.currentPlanService.isPlanActive()) {
      return this.currentPlanService.showUnlockThisFeatureModal();
    }

    try {
      await this.modalService.showMediumDialog(
        NewFolderModalComponent,
        {
          folderPath: this.folderPath,
          existingFiles: this.list(),
        },
        null,
        'newFolderButton',
      );
    } catch (error) {
      return;
    }

    this.reloadList();
  }

  selectAll() {
    this.listState.isAllSelected()
      ? this.listState.deselectAll()
      : this.listState.selectAll();
    this.selectedItemsCount.emit(this.listState.getSelected().length);
  }

  deselectAll() {
    this.listState.deselectAll();
    this.selectedItemsCount.emit(0);
  }

  sendFiles() {
    if (this.listState.getSelected().length === 0) {
      this.filesSelected.emit([
        {
          name: this.folderPath,
          kind: 'folder',
        },
      ]);
    } else {
      this.filesSelected.emit(
        this.listState.getSelected().map((file) => file.item),
      );
    }
  }

  async changeFolder(folder: ListItem<StorageFile>) {
    if (this.storageUtilsService.fileIsFolder(folder.item)) {
      if (this.storageFull) {
        this.stateService.go('apps.storage.home', {
          folderPath: folder.item.name,
        });
      } else {
        this.deselectAll();
        this.folderPath = folder.item.name;
        this.search.folderPath = folder.item.name;
        this.reloadList();
      }
    }
  }

  onFileClick(file: ListItem<StorageFile>) {
    if (
      this.storageUtilsService.fileIsFolder(file.item) &&
      file.item.name !== '/'
    ) {
      this.changeFolder(file);
    } else {
      if ((file.item as StorageFile).isThrottled) {
        (file.item as StorageFile).showThrottledCallout = !(
          file.item as StorageFile
        ).showThrottledCallout;
        // calloutClosingService.add(file);
        return;
      }

      this.onFileSelect(file);
    }
  }

  onFileSelect(file: ListItem<StorageFile>) {
    if (
      !this.storageService.canSelect(
        file,
        this.storageFull,
        this.selectorType,
        this.selectorFilter,
      )
    )
      return;
    if (
      !file.selected &&
      (this.selectorType === StorageUtilsService.SELECTOR_TYPES.SINGLE_FOLDER ||
        this.selectorType === StorageUtilsService.SELECTOR_TYPES.SINGLE_FILE)
    ) {
      this.listState.deselectAll();
    }
    this.listState.select(file);
    this.selectedItemsCount.emit(this.listState.getSelected().length);
  }

  isFileListVisible() {
    return !(
      this.loadingItems() ||
      this.listState.filesDetails.code === 202 ||
      this.isEmptyState()
    );
  }

  isEmptyState() {
    return (
      !this.folderPath &&
      this.uploadQueueLength === 0 &&
      !this.listState.filesDetails.bucketExists
    );
  }

  onHeaderResize(event: { width: number; height: number }) {
    if (this.filesListContainer) {
      this.filesListContainer.nativeElement.style.setProperty(
        '--files-list-header-height',
        event.height + 'px',
      );
    }
  }

  handleOperationClick({
    operationName,
    file,
  }: {
    operationName: string;
    file: ListItem<StorageFile>;
  }) {
    const operation = this.listOperations.operations.find(
      (operation) => operation.name === operationName,
    );
    if (operation) {
      operation.onClick &&
        operation.onClick(false, [file], undefined, this.search);
    }
  }
}
